import get from 'lodash/get';
import PropTypes from 'prop-types';

import { useTheme } from '@mui/material/styles';

import AsyncTableComponent from '../Table/AsyncTableContainer';

import CheckmarkOrXComponent from '../Table/CheckmarkOrXComponent';

import {
  formatDate,
  getUniqueValues,
  isAdmin,
  toSentenceCase,
} from '../../utils';
import { findPersonEmail, findPersonPhoneNumber } from '../Person/utils';
import { getExportTitle } from '../ViewExport/utils';
import {
  getFilterRoleOptions,
  getRoleBasedLinkToProperty,
  getUserRoles,
} from './utils';

import { TABLE_CONSTANTS, TABLE_ID } from '../Table/constants';

import { DATE_RANGES_TYPES, TABLE_DATA_TYPES } from '../../staticData';
import AdminAction from '../AdminAction';
import StyledLink from '../StyledLink';
import CellPersonWithImage from '../Table/CellPersonWithImage';
import ListActions from '../Table/ListActions';
import { limitFilter, roleFilter } from '../Table/filters';
import GlobalPersonSearchExport from './GlobalPersonSearchExport';

const GlobalPersonSearchComponent = (props) => {
  const theme = useTheme();
  const {
    items,
    onUnmount,
    getItems,
    listParams,
    listKey,
    usersCount,
    userId,
    onWipePersonData,
    onExport,
  } = props;

  const columns = [
    {
      Header: 'Name',
      id: 'first_name',
      headerClassName: 'sort',
      minWidth: 150,
      accessor: (entry) => {
        const personId = entry?.id;
        const rolesData = getUserRoles(entry, personId);
        if (rolesData?.length === 1) {
          const [role] = rolesData;
          const { url, active } = role;
          return <CellPersonWithImage person={entry} linkUrl={active && url} />;
        }
        return <CellPersonWithImage person={entry} />;
      },
    },
    {
      Header: 'Role',
      id: 'role',
      sortable: false,
      accessor: (entry) => {
        const personId = entry?.id;
        const rolesData = getUserRoles(entry, personId);
        if (!rolesData || rolesData.length < 1) return '';
        return (
          <>
            {rolesData.map(({ url, title, active }, i) => {
              if (!url || !title) return '';
              if (active) {
                return (
                  <div key={url + title + i}>
                    <StyledLink to={url}>{title}</StyledLink>
                  </div>
                );
              }
              return <div key={title + i}>{title}</div>;
            })}
          </>
        );
      },
    },
    {
      Header: 'Property',
      id: 'property',
      sortable: false,
      minWidth: 150,
      accessor: (entry, isExport) => {
        const userRoles = get(entry, 'roles');
        if (!userRoles) return '';
        const pmcProperties = userRoles.filter(
          (role) => role.propertyLevel === 'pmc',
        );
        const buildingProperties = userRoles.filter(
          (role) => role.propertyLevel !== 'pmc',
        );
        const uniqueProperties = [
          ...getUniqueValues(pmcProperties, 'pmcId'),
          ...getUniqueValues(buildingProperties, 'buildingId'),
        ];
        const properties = uniqueProperties.map((property) =>
          getRoleBasedLinkToProperty(property),
        );
        if (!properties || properties.length < 1) return '';
        return isExport ? (
          uniqueProperties
            .map(({ buildingName, pmcName }) => buildingName || pmcName)
            .join('\n')
        ) : (
          <>
            {properties.map((property, index) => (
              <div key={index}>{property}</div>
            ))}
          </>
        );
      },
    },
    {
      Header: 'E-mail',
      id: 'email',
      headerClassName: 'sort',
      minWidth: 130,
      accessor: (entry) => (
        <div title={findPersonEmail(entry)}>{findPersonEmail(entry)}</div>
      ),
    },
    {
      Header: 'Phone Number',
      id: 'phone_number',
      headerClassName: 'sort',
      accessor: (entry) => findPersonPhoneNumber(entry),
    },
    {
      Header: 'Last Activity',
      id: 'last_activity_time',
      headerClassName: 'sort',
      width: 160,
      accessor: (entry) => formatDate(entry.lastActivityTime) || 'Never',
    },
    {
      Header: 'Registration Status',
      id: 'registration_status',
      sortable: false,
      width: 120,
      filter: false,
      accessor: ({ registrationStatus }) => toSentenceCase(registrationStatus),
    },
    {
      Header: 'Active?',
      id: 'has_active_roles',
      headerClassName: 'sort',
      width: 120,
      filter: false,
      accessor: (entry) => (
        <CheckmarkOrXComponent value={!!get(entry, 'hasActiveRoles', false)} />
      ),
    },
    {
      Header: 'Actions',
      id: 'actions',
      fixed: 'right',
      sortable: false,
      width: 120,
      accessor: (entry) => {
        if (!entry?.id) return null;
        const actions = [
          {
            label: 'Delete',
            color: theme.palette.secondary.main,
            onClick: () => onWipePersonData(entry, getItems, listParams),
            isFullWidth: true,
            show: isAdmin(),
          },
        ];
        return <ListActions actions={actions} isFullWidth />;
      },
      show: isAdmin(),
    },
  ];

  const filters = [
    limitFilter(listParams),
    roleFilter(listParams, getFilterRoleOptions()),
  ];

  const action = [
    {
      label: <AdminAction>Add Admin User</AdminAction>,
      url: `/global-person-search/create`,
    },
  ];

  const defaultParams = { ordering: 'first_name' };
  const tableDataType = TABLE_DATA_TYPES.person;
  const {
    first_name,
    email,
    phone_number,
    last_activity_time,
    registered,
    has_active_roles,
    role,
    property,
  } = TABLE_CONSTANTS;
  const fieldsForBackendSort = [
    first_name,
    email,
    phone_number,
    last_activity_time,
    registered,
    has_active_roles,
    role,
    property,
  ];

  const title = 'Global Person Search';
  const exportTitle = getExportTitle(title, null, null);
  const exportActions = (
    <GlobalPersonSearchExport
      title={exportTitle}
      getDataForExport={onExport}
      columns={columns}
    />
  );

  return (
    <AsyncTableComponent
      tableId={TABLE_ID.globalPersonSearchTable}
      title={title}
      columns={columns}
      defaultSort={TABLE_CONSTANTS.first_name}
      fieldsForBackendSort={fieldsForBackendSort}
      filters={userId ? [] : filters}
      showFiltersAreApplied={false}
      items={items}
      onUnmount={onUnmount}
      getItems={getItems}
      listParams={listParams}
      defaultParams={defaultParams}
      tableDataType={tableDataType}
      listKey={listKey}
      isHideSearch={userId}
      action={userId || !isAdmin() ? null : <ListActions actions={action} />}
      dateRangeType={DATE_RANGES_TYPES.created_at}
      datePicker={!userId}
      manual
      showWithSearchOnly={!userId}
      searchPlaceholder="Start typing to see the results..."
      usersCount={usersCount}
      exportActions={exportActions}
    />
  );
};

GlobalPersonSearchComponent.defaultProps = {
  userId: null,
  usersCount: null,
};

GlobalPersonSearchComponent.propTypes = {
  onUnmount: PropTypes.func.isRequired,
  getItems: PropTypes.func.isRequired,
  items: PropTypes.instanceOf(Object).isRequired,
  listParams: PropTypes.instanceOf(Object).isRequired,
  listKey: PropTypes.string.isRequired,
  onWipePersonData: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  userId: PropTypes.string,
  usersCount: PropTypes.number,
};

export default GlobalPersonSearchComponent;
